import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useContent from '../hooks/useContent';
import useSettings from '../hooks/useSettings';

const THEME_CSS = {
  slate: `
    body { font-family: system-ui, -apple-system, sans-serif; background: #f8fafc; color: #0f172a; }
    header { background: #0f172a; color: #fff; padding: 2.5rem 1.5rem; text-align: center; }
    nav a { color: #bfdbfe; text-decoration: none; padding: 0.4rem 0.8rem; font-weight: 600; }
    article { background: #ffffff; border-radius: 16px; padding: 2.5rem; box-shadow: 0 10px 30px rgba(15,23,42,0.08); border: 1px solid #e2e8f0; }
  `,
  editorial: `
    body { font-family: Georgia, serif; background: #f5f5f4; color: #1c1917; line-height: 1.8; }
    header { background: #1c1917; color: #f5f5f4; padding: 3rem 1.5rem; text-align: center; border-bottom: 3px solid #d97706; }
    nav a { color: #f5f5f4; text-decoration: underline; font-family: sans-serif; padding: 0.4rem 0.8rem; }
    article { background: #ffffff; padding: 3rem; border: 1px solid #e7e5e4; box-shadow: none; }
    h1 { font-family: Georgia, serif; font-style: italic; }
  `,
  vibrant: `
    body { font-family: Outfit, Inter, sans-serif; background: #0f172a; color: #f8fafc; }
    header { background: linear-gradient(135deg, #4f46e5, #7c3aed); color: #fff; padding: 3.5rem 1.5rem; text-align: center; }
    nav a { color: #fff; text-decoration: none; padding: 0.5rem 1rem; border-radius: 20px; background: rgba(255,255,255,0.15); }
    article { background: rgba(30, 41, 59, 0.7); backdrop-filter: blur(12px); border-radius: 24px; padding: 2.5rem; border: 1px solid rgba(255,255,255,0.1); }
  `,
  corporate: `
    body { font-family: Helvetica, Arial, sans-serif; background: #f1f5f9; color: #0f172a; }
    header { background: #1e3a8a; color: #fff; padding: 2rem 1.5rem; text-align: center; }
    nav a { color: #ffffff; text-decoration: none; padding: 0.4rem 0.8rem; font-weight: 700; text-transform: uppercase; font-size: 0.85rem; }
    article { background: #ffffff; border-radius: 4px; padding: 2.5rem; border: 1px solid #cbd5e1; }
  `,
};

export default function PagePreview() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const { getContent, updateContent } = useContent();
  const settingsApi = useSettings();

  const [item, setItem] = useState(null);
  const [settings, setSettings] = useState({});
  const [viewport, setViewport] = useState('100%');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(true);

  const loadData = useCallback(async () => {
    if (!contentId) return;
    setLoading(true);
    setError('');
    try {
      const [contentRes, settingsRes] = await Promise.all([
        getContent(contentId),
        settingsApi.getSettings(),
      ]);
      setItem(contentRes.item);
      setSettings(settingsRes);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Live-Vorschau.');
    } finally {
      setLoading(false);
    }
  }, [contentId, getContent, settingsApi]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTitleChange = (val) => {
    setItem((prev) => (prev ? { ...prev, title: val } : prev));
  };

  const handleBlockChange = (blockIndex, field, val) => {
    setItem((prev) => {
      if (!prev) return prev;
      const blocks = [...(prev.body?.blocks || [])];
      if (!blocks[blockIndex]) return prev;
      blocks[blockIndex] = {
        ...blocks[blockIndex],
        props: { ...blocks[blockIndex].props, [field]: val },
      };
      return { ...prev, body: { ...prev.body, blocks } };
    });
  };

  const handleSave = async () => {
    if (!item) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const updateRes = await updateContent(contentId, item);
      if (updateRes && updateRes.item) {
        setItem(updateRes.item);
      }
      await new Promise((r) => setTimeout(r, 1000));
      setMessage('Änderungen erfolgreich gespeichert.');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
        <p>Lade In-Context Live-Vorschau…</p>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div style={{ padding: '2rem' }}>
        <div className="alert error">{error || 'Inhalt konnte nicht gefunden werden.'}</div>
        <button type="button" className="button secondary" style={{ marginTop: '1rem' }} onClick={() => navigate('/content')}>
          ← Zurück zur Übersicht
        </button>
      </div>
    );
  }

  const activeTheme = settings.theme || 'slate';
  const customCss = THEME_CSS[activeTheme] || THEME_CSS.slate;
  const blocks = item.body?.blocks || [];

  return (
    <div style={{ background: '#020617', minHeight: '100vh', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Sticky Control Topbar */}
      <div
        style={{
          background: '#0f172a',
          borderBottom: '1px solid #1e293b',
          padding: '0.75rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        <div className="row" style={{ gap: '0.75rem', alignItems: 'center' }}>
          <button type="button" className="button ghost small" onClick={() => navigate(`/content/${contentId}`)}>
            ← Formular-Editor
          </button>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#f8fafc' }}>
            Live In-Context Editor
          </span>
          <span className="badge" style={{ background: 'rgba(37,99,235,0.2)', color: '#60a5fa' }}>
            Theme: {activeTheme}
          </span>
        </div>

        {/* Viewport Switcher Controls */}
        <div className="segmented-control" style={{ background: '#1e293b' }}>
          <button
            type="button"
            className={viewport === '100%' ? 'active' : ''}
            onClick={() => setViewport('100%')}
          >
            🖥️ Desktop
          </button>
          <button
            type="button"
            className={viewport === '768px' ? 'active' : ''}
            onClick={() => setViewport('768px')}
          >
            📱 Tablet
          </button>
          <button
            type="button"
            className={viewport === '375px' ? 'active' : ''}
            onClick={() => setViewport('375px')}
          >
            📲 Mobile
          </button>
        </div>

        {/* Action Controls */}
        <div className="row" style={{ gap: '0.75rem', alignItems: 'center' }}>
          <button
            type="button"
            className={`button ${editMode ? 'primary' : 'ghost'} small`}
            onClick={() => setEditMode((v) => !v)}
          >
            {editMode ? '✏️ Direct Inline-Edit Mode' : '👁️ Preview Only Mode'}
          </button>

          <button type="button" className="button primary small" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichert…' : 'Änderungen Speichern'}
          </button>
        </div>
      </div>

      {message ? <div className="alert success" style={{ margin: '0.5rem 1.5rem 0' }}>{message}</div> : null}
      {error ? <div className="alert error" style={{ margin: '0.5rem 1.5rem 0' }}>{error}</div> : null}

      {/* Live Preview Container Canvas */}
      <div style={{ flexGrow: 1, padding: '2rem 1rem', display: 'flex', justifyContent: 'center', background: '#090d16' }}>
        <div
          style={{
            width: viewport,
            maxWidth: '100%',
            transition: 'width 0.3s ease',
            background: '#ffffff',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            borderRadius: viewport === '100%' ? '0' : '16px',
            overflow: 'hidden',
          }}
        >
          {/* Inject Dynamic Theme Styles */}
          <style>{customCss}</style>

          {/* Header & Navigation */}
          <header>
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
              <h1 style={{ margin: 0 }}>{settings.siteTitle || 'Headless CMS'}</h1>
              <p style={{ margin: '0.5rem 0 1.5rem', opacity: 0.8 }}>{settings.siteDescription || 'Publizierte Inhalte'}</p>
              <nav style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                {(settings.navLinks || []).map((link) => (
                  <a key={link.id || link.href} href={link.href} target={link.target}>
                    {link.label}
                  </a>
                ))}
              </nav>
            </div>
          </header>

          {/* Main Article Body */}
          <main style={{ maxWidth: '860px', margin: '0 auto', padding: '3rem 1.5rem' }}>
            <article style={{ position: 'relative' }}>
              {/* Meta information */}
              <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.5rem', textTransform: 'uppercase' }}>
                {item.contentType} · Live In-Context Rendering
              </div>

              {/* Editable Page Title */}
              {editMode ? (
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  style={{
                    fontSize: '2.25rem',
                    fontWeight: 800,
                    width: '100%',
                    border: '1px dashed #2563eb',
                    background: 'rgba(37,99,235,0.05)',
                    padding: '0.4rem 0.6rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    color: 'inherit',
                    fontFamily: 'inherit',
                  }}
                  title="Klicke zum Bearbeiten des Titels"
                />
              ) : (
                <h1 style={{ fontSize: '2.25rem', fontWeight: 800, margin: '0 0 1.5rem' }}>{item.title}</h1>
              )}

              {/* Featured Image */}
              {item.featuredImage?.url && (
                <img
                  src={item.featuredImage.url}
                  alt={item.featuredImage.alt || item.title}
                  style={{ width: '100%', borderRadius: '12px', marginBottom: '2rem' }}
                />
              )}

              {/* Render Blocks Inline */}
              <div className="post-blocks" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {blocks.map((block, index) => {
                  const props = block.props || {};

                  if (block.type === 'heading') {
                    return editMode ? (
                      <input
                        key={block.id || index}
                        type="text"
                        value={props.text || ''}
                        onChange={(e) => handleBlockChange(index, 'text', e.target.value)}
                        style={{
                          fontSize: props.level === 1 ? '2rem' : props.level === 3 ? '1.3rem' : '1.6rem',
                          fontWeight: 700,
                          border: '1px dashed #3b82f6',
                          background: 'rgba(59,130,246,0.05)',
                          padding: '0.4rem',
                          borderRadius: '6px',
                          width: '100%',
                          color: 'inherit',
                          fontFamily: 'inherit',
                        }}
                      />
                    ) : (
                      <h2 key={block.id || index}>{props.text}</h2>
                    );
                  }

                  if (block.type === 'text') {
                    return editMode ? (
                      <textarea
                        key={block.id || index}
                        rows={4}
                        value={props.html || ''}
                        onChange={(e) => handleBlockChange(index, 'html', e.target.value)}
                        style={{
                          width: '100%',
                          border: '1px dashed #3b82f6',
                          background: 'rgba(59,130,246,0.05)',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          fontFamily: 'inherit',
                          fontSize: '1rem',
                          color: 'inherit',
                        }}
                      />
                    ) : (
                      <div key={block.id || index} dangerouslySetInnerHTML={{ __html: props.html || '' }} />
                    );
                  }

                  if (block.type === 'quote') {
                    return (
                      <blockquote key={block.id || index} style={{ borderLeft: '4px solid #2563eb', paddingLeft: '1rem', fontStyle: 'italic' }}>
                        {editMode ? (
                          <input
                            type="text"
                            value={props.html || ''}
                            onChange={(e) => handleBlockChange(index, 'html', e.target.value)}
                            style={{ width: '100%', border: '1px dashed #2563eb', background: 'transparent' }}
                          />
                        ) : (
                          props.html
                        )}
                      </blockquote>
                    );
                  }

                  if (block.type === 'divider') {
                    return <hr key={block.id || index} style={{ border: 0, height: 1, background: '#e2e8f0', margin: '2rem 0' }} />;
                  }

                  return (
                    <div key={block.id || index} style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }}>
                        Block: {block.type}
                      </span>
                    </div>
                  );
                })}
              </div>
            </article>
          </main>

          <footer style={{ textAlign: 'center', padding: '2.5rem 1.5rem', borderTop: '1px solid #e2e8f0', color: '#64748b' }}>
            &copy; {new Date().getFullYear()} {settings.siteTitle || 'Headless CMS'}. Live In-Context Preview.
          </footer>
        </div>
      </div>
    </div>
  );
}
